import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { selectCurrentCompany } from "../../../../redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "../../../../redux/hooks/redux";
import { Avatars, Chip } from "../../components/bits";
import { Icon } from "../../components/Icon";
import { Confirm } from "../../components/Shell";
import { companySuggestionDate, getRealSuggestions, type BackendSuggestion } from "../../data/api";
import { availabilitySnapshot, NO_LOCATION } from "../../data/realDay";
import type { ServiceType, SmartSuggestion, SpaceRequest } from "../../data/types";
import type { Snapshot } from "../../lib/scheduling";
import { evaluate, findSlots, nextDays, proposeMove, weekAvailability, type MovePlan } from "../../lib/scheduling";
import { addDays, dayLabel, fmtHour } from "../../lib/time";
import { tint, useStore } from "../../state/store";

const EMPTY: Snapshot = {
  today: new Date().toISOString().slice(0, 10),
  now: 0,
  teams: [],
  visits: [],
  blocks: [],
  clients: [],
  closedDays: [],
};

const SERVICES: { id: ServiceType; label: string; hours: number }[] = [
  { id: "regular", label: "Regular", hours: 2 },
  { id: "profunda", label: "Profunda", hours: 4 },
  { id: "oficina", label: "Oficina", hours: 2.5 },
  { id: "mudanza", label: "Mudanza", hours: 6 },
];
const X = (h: number) => ((h - 8) / 10) * 100;

/**
 * “¿Hay espacio?” — flujo de llamada:
 *   1) ¿Hay espacio directo?  2) ¿Puedo reajustar un trabajo?  3) Otras fechas.
 * El sistema propone el paso que aplica; el agente puede saltar a otro.
 */
export function SpacePage() {
  const s = useStore();
  const location = useLocation();
  const zone = useAppSelector(selectCurrentCompany)?.timeZone;
  const jobId = Number(new URLSearchParams(location.search).get("jobId"));
  const validZone = !!zone && DateTime.now().setZone(zone).isValid;
  const navigate = useNavigate();
  // Con “Mis visitas reales” se buscan huecos en la agenda real (solo lectura).
  const isReal = s.dataSource === "real";
  const realDay = s.real.day;
  const realSnap = useMemo(() => (isReal && realDay ? availabilitySnapshot(realDay) : null), [isReal, realDay]);
  const snap = isReal ? (realSnap ?? EMPTY) : s.snapshot;
  const [name, setName] = useState(s.caller?.name ?? (isReal ? "" : "Paola Ruiz"));
  const [address, setAddress] = useState(s.caller?.address ?? (isReal ? "" : "1420 Oak St"));
  const [service, setService] = useState<ServiceType>("regular");
  const [duration, setDuration] = useState(2);
  const [when, setWhen] = useState<SpaceRequest["when"]>("hoy");
  const [forced, setForced] = useState<1 | 2 | 3 | null>(null);
  const [reserve, setReserve] = useState<SmartSuggestion | null>(null);
  const [move, setMove] = useState<MovePlan | null>(null);
  const [more, setMore] = useState(false);

  const req: SpaceRequest = {
    clientName: name,
    address,
    location: isReal ? NO_LOCATION : { lat: 30.252, lng: -97.752 },
    service,
    durationH: duration,
    when,
  };
  const auto = useMemo(() => evaluate(snap, req), [snap, service, duration, when]); // eslint-disable-line react-hooks/exhaustive-deps
  const step = forced ?? auto.step;
  const day = when === "manana" ? addDays(snap.today, 1) : snap.today;
  const direct = step === 1 ? (auto.step === 1 ? auto.options : findSlots(snap, req, day).slice(0, 3)) : [];
  const plan = step === 2 ? (auto.step === 2 ? auto.plan : proposeMove(snap, req, day)) : null;
  const later = step === 3 ? (auto.step === 3 ? auto.options : nextDays(snap, req, 1)) : [];

  if (isReal && !realDay) {
    return (
      <div className="content">
        <header className="row wrap" style={{ justifyContent: "space-between" }}>
          <div>
            <div className="eyebrow">Agenda inteligente</div>
            <h1 className="h1">¿Hay espacio?</h1>
          </div>
          <Chip bg="#F0EFEB" fg="#3A3A3D">
            Tu agenda real · solo lectura
          </Chip>
        </header>
        <section className="card" style={{ padding: 24, marginTop: 16 }}>
          <p className="muted" style={{ margin: 0 }}>
            {s.real.status === "error" ? 'No se pudieron leer las visitas reales. Prueba "Actualizar".' : "Leyendo agenda real..."}
          </p>
          {s.real.status === "error" && (
            <button type="button" className="btn sm pri" style={{ marginTop: 12 }} onClick={s.reloadReal}>
              Actualizar
            </button>
          )}
        </section>
      </div>
    );
  }
  // TODO(API): new-service suggestions need an endpoint that does not require jobId;
  // move proposals need client flexibility and an atomic server-side simulation.

  return (
    <div className="content">
      <header className="row wrap" style={{ justifyContent: "space-between" }}>
        <div>
          <div className="eyebrow">Agenda inteligente</div>
          <h1 className="h1">¿Hay espacio?</h1>
        </div>
        <Chip bg="#F0EFEB" fg="#3A3A3D">
          {isReal ? "Tu agenda real · solo lectura" : "Vista previa"}
        </Chip>
      </header>

      <nav className="row wrap" aria-label="Pasos" style={{ gap: 8 }}>
        {([1, 2, 3] as const).map((n) => (
          <span key={n} className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className={`step${step === n ? " on" : n < step ? " done" : ""}`}
              onClick={() => setForced(n === auto.step ? null : n)}
            >
              <span className="n">{n < step ? <Icon name="check" color="#166534" size={13} /> : n}</span>
              {n === 1 ? "¿Hay espacio?" : n === 2 ? "¿Puedo reajustar?" : "Otras fechas"}
            </button>
            {n < 3 && <Icon name="next" color="#A3A4A8" size={16} />}
          </span>
        ))}
      </nav>

      <div className="row wrap" style={{ alignItems: "flex-start", gap: 18 }}>
        <form
          className="w"
          style={{ flex: "0 1 360px", minWidth: 300, gap: 16 }}
          onSubmit={(e) => e.preventDefault()}
          aria-label="Datos de la llamada"
        >
          <div className="row">
            <span
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: "#E9F7EF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="phone" color="#16603D" size={20} />
            </span>
            <div>
              <b style={{ fontSize: 16 }}>{s.caller ? "Llamada en curso" : "Nuevo servicio"}</b>
              <div className="muted" style={{ fontSize: 12 }}>
                {s.caller?.phone ?? "Flow Mobile"}
              </div>
            </div>
          </div>
          <label className="field">
            <span>Cliente</span>
            <input className="inp" value={name} placeholder="Nombre" onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span>Dirección</span>
            <input className="inp" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <span>Servicio</span>
            <div className="row wrap" style={{ gap: 6 }}>
              {SERVICES.map((sv) => (
                <button
                  key={sv.id}
                  type="button"
                  className={`pill${service === sv.id ? " on" : ""}`}
                  aria-pressed={service === sv.id}
                  onClick={() => {
                    setService(sv.id);
                    setDuration(sv.hours);
                    setForced(null);
                  }}
                >
                  <Icon name={sv.id} size={16} />
                  {sv.label}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="field">
            <span>Duración (h)</span>
            <input
              className="inp"
              type="number"
              min={1}
              max={9}
              step={0.5}
              value={duration}
              onChange={(e) => {
                setDuration(Number(e.target.value) || 1);
                setForced(null);
              }}
            />
          </label>
          <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
            <span>¿Cuándo?</span>
            <div className="row wrap" style={{ gap: 6 }}>
              {(
                [
                  ["hoy", "Hoy"],
                  ["manana", "Mañana"],
                  ["semana", "Esta semana"],
                ] as const
              ).map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  className={`pill${when === v ? " on" : ""}`}
                  aria-pressed={when === v}
                  onClick={() => {
                    setWhen(v);
                    setForced(null);
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </fieldset>
        </form>

        <section className="w" style={{ flex: "999 1 560px", padding: 22, gap: 18 }} aria-live="polite">
          {Number.isSafeInteger(jobId) && jobId > 0 ? (
            validZone ? (
              <RealOptions jobId={jobId} zone={zone} when={when} duration={duration} />
            ) : (
              <div className="muted">No se pueden consultar opciones reales: falta una zona horaria válida de la empresa.</div>
            )
          ) : isReal && !realDay ? (
            <div className="muted" role="status">
              {s.real.status === "error" ? "No se pudo leer tu agenda. Vuelve a intentarlo desde Agenda → Actualizar." : "Leyendo tu agenda…"}
            </div>
          ) : (
            <>
              {isReal && (
                <div className="muted" style={{ fontSize: 13 }}>
                  Busca en tu agenda real de hoy a {realDay?.days ?? 7} días. Solo cuenta a quien ya tiene visitas ese día, con jornada de 8:00 a
                  18:00. La distancia todavía no se calcula.
                </div>
              )}
              {step === 1 &&
                (direct.length ? (
                  <>
                    <Verdict
                      tone="ok"
                      icon="check"
                      title={when === "semana" ? "Hay espacio esta semana" : `Sí, hay espacio ${when === "hoy" ? "hoy" : "mañana"}`}
                      sub={`Mejor opción cerca de ${address}`}
                    />
                    {direct.slice(0, more ? 3 : 1).map((o, i) => (
                      <Option key={o.teamId + o.date} o={o} snap={snap} real={isReal} best={i === 0} onPick={() => pick(o)} />
                    ))}
                    {direct.length > 1 && (
                      <button type="button" className="btn sm" style={{ alignSelf: "flex-start" }} onClick={() => setMore((m) => !m)}>
                        {more ? "Ver menos" : `Ver ${direct.length - 1} opciones más`}
                      </button>
                    )}
                  </>
                ) : (
                  <Verdict tone="none" icon="x" title="No hay espacio directo" sub="Prueba el paso 2: reajustar un trabajo." />
                ))}

              {step === 2 &&
                (plan ? (
                  <MoveView plan={plan} snap={snap} onApply={() => setMove(plan)} />
                ) : (
                  <Verdict
                    tone="none"
                    icon="x"
                    title="No hay un trabajo que se pueda mover"
                    sub={
                      isReal
                        ? "Reajustar con tu agenda real llega cuando FlowNect marque qué clientes aceptan cambios. Pasa a otras fechas."
                        : "Solo se mueven clientes marcados como flexibles. Pasa a otras fechas."
                    }
                  />
                ))}

              {step === 3 && (
                <LaterView
                  req={req}
                  snap={snap}
                  real={isReal}
                  options={later.slice(0, more ? 3 : 1)}
                  total={later.length}
                  more={more}
                  onMore={() => setMore((m) => !m)}
                  onPick={pick}
                />
              )}
            </>
          )}
        </section>
      </div>

      {reserve && (
        <Confirm
          title="Reservar (simulado)"
          confirmLabel="Reservar en la vista previa"
          onCancel={() => setReserve(null)}
          onConfirm={() => {
            s.addVisit({
              uid: `new-${Date.now()}`,
              date: reserve.date,
              teamId: reserve.teamId,
              clientUid: "paola",
              service,
              start: reserve.start,
              end: reserve.end,
              status: "scheduled",
              assignments: [],
              updatedAt: new Date().toISOString(),
            });
            setReserve(null);
            s.notify("Reservado en la vista previa. En producción se crea la visita y se prepara la confirmación al cliente.");
            s.go("agenda");
          }}
        >
          <p style={{ margin: 0 }}>
            <b>{name}</b> · {snapTeam(reserve.teamId)} · {dayLabel(reserve.date, snap.today)} {fmtHour(reserve.start)}–{fmtHour(reserve.end)}
          </p>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            No se envía ningún mensaje desde esta vista previa.
          </p>
        </Confirm>
      )}
      {move && (
        <Confirm
          title="Mover y reservar (simulado)"
          confirmLabel="Aplicar en la vista previa"
          onCancel={() => setMove(null)}
          onConfirm={() => {
            s.updateVisit(move.moved.uid, {
              teamId: move.movedTo.teamId,
              start: move.movedTo.start,
              end: move.movedTo.end,
              updatedAt: new Date().toISOString(),
            });
            s.addVisit({
              uid: `new-${Date.now()}`,
              date: move.slot.date,
              teamId: move.slot.teamId,
              clientUid: "paola",
              service,
              start: move.slot.start,
              end: move.slot.end,
              status: "scheduled",
              assignments: [],
              updatedAt: new Date().toISOString(),
            });
            setMove(null);
            s.notify("Aplicado en la vista previa. El aviso al cliente movido queda preparado, no enviado.");
            s.go("agenda");
          }}
        >
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Esto se enviaría al servidor. Si la visita cambió, responde 409 y se muestra “La visita cambió”.
          </p>
          <pre className="code">{JSON.stringify(move.request, null, 2)}</pre>
        </Confirm>
      )}
    </div>
  );

  function snapTeam(id: string) {
    return snap.teams.find((t) => t.id === id)?.name ?? id;
  }

  /** Demo: reserva simulada. Real: no se reserva aquí; se crea el trabajo en Trabajos. */
  function pick(o: SmartSuggestion) {
    if (!isReal) {
      setReserve(o);
      return;
    }
    s.notify(
      `${snapTeam(o.teamId)} · ${dayLabel(o.date, snap.today)} ${fmtHour(o.start)}–${fmtHour(o.end)}. Crea el trabajo en Trabajos con este horario.`,
    );
    navigate("/business/jobs");
  }
}

/** Read-only suggestions for an existing job. The server requires jobId and returns
 * UNVERIFIED ranked candidates; no result here is a reservation or arrival promise. */
function RealOptions({ jobId, zone, when, duration }: { jobId: number; zone: string; when: SpaceRequest["when"]; duration: number }) {
  const [options, setOptions] = useState<BackendSuggestion[] | null>(null);
  const [error, setError] = useState(false);
  const [more, setMore] = useState(false);
  const date = companySuggestionDate(DateTime.now(), zone, when);
  useEffect(() => {
    let active = true;
    setOptions(null);
    setError(false);
    getRealSuggestions(jobId, date, duration, when === "semana" ? 7 : 1)
      .then((data) => {
        if (active) setOptions(data);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [jobId, date, duration, when]);
  return (
    <>
      <div className="eyebrow">Job existente #{jobId} · datos reales de agenda</div>
      <div className="muted">
        Estas opciones se calculan para ese job, según sus requisitos guardados. El cliente y la dirección escritos a la izquierda pertenecen a la
        vista previa.
      </div>
      {error ? (
        <div role="alert">No se pudieron consultar las sugerencias. Revisa los requisitos del job y vuelve a intentarlo.</div>
      ) : options === null ? (
        <div>Consultando agenda…</div>
      ) : options.length === 0 ? (
        <div>No hay opciones para este periodo.</div>
      ) : (
        options.slice(0, more ? options.length : 1).map((o) => {
          const at = DateTime.fromISO(o.schedule.startDate, { setZone: true }).setZone(zone);
          return (
            <div key={`${o.rank}:${o.schedule.startDate}`} className="opt">
              <div>
                <b>{at.toFormat("ccc d · h:mm a")}</b>
                <div className="muted">
                  {o.workforce.employees.length + o.workforce.contractors.length + o.workforce.supervisors.length} personas · rango #{o.rank}
                </div>
              </div>
              <div className="muted">
                {o.routeReadiness.status === "LOCATIONS_READY" ? "Ruta: ubicaciones completas" : "Ruta: datos faltantes"} · Factibilidad sin verificar
              </div>
            </div>
          );
        })
      )}
      {options && options.length > 1 && (
        <button type="button" className="btn sm" onClick={() => setMore((x) => !x)}>
          {more ? "Ver menos" : `Ver ${options.length - 1} opciones más`}
        </button>
      )}
      <div className="muted">Ninguna opción se reserva ni mueve automáticamente.</div>
    </>
  );
}

function Verdict({ tone, icon, title, sub }: { tone: "ok" | "swap" | "none"; icon: string; title: string; sub: string }) {
  const c = { ok: ["#16A34A", "#DCFCE7"], swap: ["#B7791F", "#FFF1D6"], none: ["#5E5F63", "#F0EFEB"] }[tone];
  return (
    <div className="verdict">
      <div className="ico" style={{ background: c[1] }}>
        <Icon name={icon} color={c[0]} size={34} />
      </div>
      <div>
        <h2>{title}</h2>
        <span className="muted" style={{ fontSize: 15 }}>
          {sub}
        </span>
      </div>
    </div>
  );
}

function Option({ o, snap, real, best, onPick }: { o: SmartSuggestion; snap: Snapshot; real: boolean; best: boolean; onPick(): void }) {
  const team = snap.teams.find((t) => t.id === o.teamId)!;
  const busy = snap.visits.filter((v) => v.teamId === o.teamId && v.date === o.date && v.status !== "cancelled");
  return (
    <div className={`opt${best ? " best" : ""}`}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: "0 0 70px" }}>
        <Avatars team={team} color={team.color} size={26} />
        <b style={{ fontSize: 12, color: team.color }}>{team.name}</b>
      </div>
      <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="row wrap">
          <b style={{ fontSize: 24, letterSpacing: "-.02em" }}>
            {o.date === snap.today ? "" : `${dayLabel(o.date, snap.today)} · `}
            {fmtHour(o.start)}
          </b>
          {!real && (
            <Chip bg="#F0EFEB" fg="#3A3A3D">
              <Icon name="car" color="#3A3A3D" size={14} />
              {o.travelDistanceMiles} mi
            </Chip>
          )}
          {best && (
            <Chip bg="#DCFCE7" fg="#166534">
              <Icon name="bolt" color="#166534" size={13} />
              {real ? "Lo más pronto" : "Mejor"}
            </Chip>
          )}
          {!real && (
            <Chip
              bg={o.routeReadiness === "ready" ? "#E9F7EF" : "#FFF"}
              fg={o.routeReadiness === "ready" ? "#166534" : "#5E5F63"}
              style={o.routeReadiness === "ready" ? undefined : { border: "1.5px dashed #8A8B8E" }}
            >
              {o.routeReadiness === "ready" ? "Ruta: ubicaciones completas" : "Ruta incompleta"}
            </Chip>
          )}
        </div>
        <div className="mini" style={{ height: 14 }} aria-hidden="true">
          {busy.map((v) => (
            <i key={v.uid} style={{ left: `${X(v.start)}%`, width: `${X(v.end) - X(v.start)}%`, background: tint(team.color, 0.45) }} />
          ))}
          <i style={{ left: `${X(o.start)}%`, width: `${X(o.end) - X(o.start)}%`, background: "#16A34A" }} />
        </div>
      </div>
      <button type="button" className={`btn${best ? " pri" : ""}`} onClick={onPick}>
        {real ? "Crear en Trabajos" : "Reservar"}
      </button>
    </div>
  );
}

function MoveView({ plan, snap, onApply }: { plan: MovePlan; snap: Snapshot; onApply(): void }) {
  const moved = snap.clients.find((c) => c.uid === plan.moved.clientUid);
  const teamsInvolved = Array.from(new Set([plan.slot.teamId, plan.moved.teamId, plan.movedTo.teamId]));
  const lane = (teamId: string, after: boolean) => {
    const team = snap.teams.find((t) => t.id === teamId)!;
    const vs = snap.visits.filter((v) => v.teamId === teamId && v.date === plan.slot.date && v.status !== "cancelled");
    const items: { start: number; end: number; label: string; kind: "x" | "new" | "moved" }[] = [];
    for (const v of vs) {
      if (after && v.uid === plan.moved.uid) continue;
      items.push({ start: v.start, end: v.end, label: snap.clients.find((c) => c.uid === v.clientUid)?.name ?? "", kind: "x" });
    }
    if (after && teamId === plan.movedTo.teamId) items.push({ ...plan.movedTo, label: moved?.name ?? "", kind: "moved" });
    if (after && teamId === plan.slot.teamId) items.push({ start: plan.slot.start, end: plan.slot.end, label: "Nuevo cliente", kind: "new" });
    return (
      <div className="row" key={teamId + after}>
        <b style={{ flex: "0 0 86px", color: team.color }}>{team.name}</b>
        <div className="trk" style={{ height: 46, background: "#FAFAF8", borderRadius: 12 }}>
          {items.map((it) => (
            <div
              key={it.label + it.start}
              className="blk"
              style={{
                left: `${X(it.start)}%`,
                width: `${X(it.end) - X(it.start)}%`,
                top: 6,
                bottom: 6,
                cursor: "default",
                background: it.kind === "new" ? "#16A34A" : tint(team.color),
                color: it.kind === "new" ? "#fff" : "#1F1F21",
                boxShadow: it.kind === "moved" ? "inset 0 0 0 2px #FAAF3B" : undefined,
              }}
            >
              {it.label}
            </div>
          ))}
        </div>
      </div>
    );
  };
  const from = snap.teams.find((t) => t.id === plan.moved.teamId)!;
  const to = snap.teams.find((t) => t.id === plan.movedTo.teamId)!;
  return (
    <>
      <Verdict
        tone="swap"
        icon="swap"
        title="No hay hueco… pero se puede mover 1 trabajo"
        sub={`Así queda: el nuevo cliente entra a las ${fmtHour(plan.slot.start)}`}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, borderRadius: 16, background: "#FAFAF8" }}>
        <Chip bg="#fff" fg="#1F1F21" style={{ alignSelf: "flex-start" }}>
          Ahora
        </Chip>
        {teamsInvolved.map((t) => lane(t, false))}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: 14,
          borderRadius: 16,
          background: "#F2FBF6",
          boxShadow: "inset 0 0 0 2px #86EFAC",
        }}
      >
        <Chip bg="#16A34A" fg="#fff" style={{ alignSelf: "flex-start" }}>
          Después
        </Chip>
        {teamsInvolved.map((t) => lane(t, true))}
      </div>
      <div className="row wrap">
        <Chip bg="#DCFCE7" fg="#166534">
          <Icon name="check" color="#166534" size={13} />
          {moved?.name} acepta cambios
        </Chip>
        <Chip bg="#F0EFEB" fg="#3A3A3D">
          {moved?.name}: {from.name} {fmtHour(plan.moved.start)} → {to.name} {fmtHour(plan.movedTo.start)}
        </Chip>
      </div>
      <div className="row wrap" style={{ borderTop: "1px solid #F0EFEB", paddingTop: 14 }}>
        <button type="button" className="btn pri" onClick={onApply}>
          Mover y reservar
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          El aviso a {moved?.name} queda preparado, no se envía solo.
        </span>
      </div>
    </>
  );
}

function LaterView({
  req,
  snap,
  real,
  options,
  total,
  more,
  onMore,
  onPick,
}: {
  req: SpaceRequest;
  snap: Snapshot;
  real: boolean;
  options: SmartSuggestion[];
  total: number;
  more: boolean;
  onMore(): void;
  onPick(o: SmartSuggestion): void;
}) {
  const s = useStore();
  const week = weekAvailability(snap, req);
  return (
    <>
      <Verdict
        tone="none"
        icon="x"
        title={req.when === "hoy" ? "Hoy no se puede" : "No en ese día"}
        sub={real ? `Nadie tiene ${req.durationH} h libres ese día` : `Ningún equipo tiene ${req.durationH} h libres, ni moviendo trabajos`}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <b style={{ fontSize: 15 }}>Lo más pronto</b>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 8 }}>
          {week.map((d) => (
            <div
              key={d.date}
              className="card"
              style={{
                padding: "12px 6px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                opacity: d.count ? 1 : 0.55,
                boxShadow: options[0]?.date === d.date ? "inset 0 0 0 2px #1F1F21" : undefined,
                background: options[0]?.date === d.date ? "#FFF7E3" : "#fff",
              }}
            >
              <span className="muted" style={{ fontSize: 12, fontWeight: 700 }}>
                {dayLabel(d.date, snap.today)}
              </span>
              <div className="row" style={{ gap: 4, minHeight: 12 }}>
                {d.count ? (
                  Array.from({ length: d.count }, (_, i) => (
                    <span key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: "#16A34A" }} />
                  ))
                ) : (
                  <span style={{ fontSize: 11, color: "#A3A4A8", fontWeight: 700 }}>sin espacio</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {options.map((o, i) => (
        <Option key={o.date + o.teamId} o={o} snap={snap} real={real} best={i === 0} onPick={() => onPick(o)} />
      ))}
      {total > 1 && (
        <button type="button" className="btn sm" style={{ alignSelf: "flex-start" }} onClick={onMore}>
          {more ? "Ver menos" : `Ver ${total - 1} fechas más`}
        </button>
      )}
      {!real && (
        <div className="row wrap" style={{ borderTop: "1px solid #F0EFEB", paddingTop: 14 }}>
          <button type="button" className="btn dark" onClick={() => s.notify("Opciones preparadas para enviar al cliente; tú confirmas el envío.")}>
            Ofrecer estas opciones
          </button>
          <button
            type="button"
            className="btn"
            onClick={() =>
              s.showSoon({ title: "Lista de espera", body: "Avisar al cliente si se libera un espacio hoy. Requiere API de notificaciones." })
            }
          >
            Lista de espera para hoy
          </button>
        </div>
      )}
    </>
  );
}
